import { playbackSteps } from '@trips/shared/playback';
import { lazy, Suspense, useMemo, useState } from 'react';
import { selectItemsByTrip, selectPlaceLookup, selectTripSummaries } from '../../data/selectors';
import { useStore } from '../../data/store';
import { EmptyState } from '../../ui/EmptyState';
import { Page } from '../../ui/Page';
import { Segmented } from '../../ui/Segmented';

const MapPlayback = lazy(() => import('./MapPlayback'));

type Filter = 'all' | 'past' | 'upcoming';

/** Every trip, played back in order on one map. */
export function MapScreen() {
  const trips = useStore(selectTripSummaries);
  const byTrip = useStore(selectItemsByTrip);
  const places = useStore(selectPlaceLookup);
  const [filter, setFilter] = useState<Filter>('all');

  const steps = useMemo(() => {
    const shown = trips.filter((t) =>
      filter === 'all' ? true : filter === 'past' ? t.phase === 'past' : t.phase !== 'past',
    );
    const items = shown.flatMap((t) => byTrip.get(t.id) ?? []);
    return playbackSteps(items, places);
  }, [trips, byTrip, places, filter]);

  return (
    <Page title="All trips" backTo="/" wide>
      <Segmented
        label="Which trips"
        value={filter}
        onChange={setFilter}
        options={[
          { value: 'all', label: 'All' },
          { value: 'past', label: 'Past' },
          { value: 'upcoming', label: 'Upcoming' },
        ]}
      />
      {steps.length === 0 ? (
        <EmptyState icon="map-trifold" title="Your travels will play here">
          <p>Flights, drives, stays and cruises from every trip, in order.</p>
        </EmptyState>
      ) : (
        <Suspense fallback={<div style={{ height: 'min(62vh, 560px)' }} />}>
          <MapPlayback key={filter} steps={steps} defaultSpeed={4} label="trips" />
        </Suspense>
      )}
    </Page>
  );
}
